import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import HowItWorks from "@/pages/HowItWorks";
import Generator from "@/pages/Generator";
import Templates from "@/pages/Templates";
import Printers from "@/pages/Printers";
import SSO from "@/pages/SSO";
import AppConfig from "@/pages/AppConfig";
import CustomSoftware from "@/pages/CustomSoftware";
import AutoPkg from "@/pages/AutoPkg";
import ForgePkg from "@/pages/ForgePkg";
import MenuBarForge from "@/pages/MenuBarForge";
import RemoteDesktop from "@/pages/RemoteDesktop";
import Settings from "@/pages/Settings";
import Knowledge from "@/pages/Knowledge";
import WhatsNew from "@/pages/WhatsNew";
import Catalog from "@/pages/Catalog";
import Projects from "@/pages/Projects";
import Admin from "@/pages/Admin";
import PackageLibrary from "@/pages/PackageLibrary";

function ProtectedRoute({ children }) {
  const { user } = useAuth();
  if (user === null)
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0B]">
        <span className="font-mono text-sm text-[#00F0FF]">
          initializing<span className="cursor-blink">_</span>
        </span>
      </div>
    );
  if (user === false) return <Navigate to="/login" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user && user !== false) return <Navigate to="/app" replace />;
  return children;
}

function AdminRoute({ children }) {
  const { user } = useAuth();
  if (user === null)
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0B]">
        <span className="font-mono text-sm text-[#00F0FF]">
          initializing<span className="cursor-blink">_</span>
        </span>
      </div>
    );
  if (user === false) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/app" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
            <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/app" element={<ProtectedRoute><Generator /></ProtectedRoute>} />
            <Route path="/app/templates" element={<ProtectedRoute><Templates /></ProtectedRoute>} />
            <Route path="/app/printers" element={<ProtectedRoute><Printers /></ProtectedRoute>} />
            <Route path="/app/sso" element={<ProtectedRoute><SSO /></ProtectedRoute>} />
            <Route path="/app/appconfig" element={<ProtectedRoute><AppConfig /></ProtectedRoute>} />
            <Route path="/app/custom-software" element={<ProtectedRoute><CustomSoftware /></ProtectedRoute>} />
            <Route path="/app/autopkg" element={<ProtectedRoute><AutoPkg /></ProtectedRoute>} />
            <Route path="/app/forgepkg" element={<ProtectedRoute><ForgePkg /></ProtectedRoute>} />
            <Route path="/app/menubar" element={<ProtectedRoute><MenuBarForge /></ProtectedRoute>} />
            <Route path="/app/remote-desktop" element={<ProtectedRoute><RemoteDesktop /></ProtectedRoute>} />
            <Route path="/app/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
            <Route path="/app/knowledge" element={<ProtectedRoute><Knowledge /></ProtectedRoute>} />
            <Route path="/app/whats-new" element={<ProtectedRoute><WhatsNew /></ProtectedRoute>} />
            <Route path="/app/catalog" element={<ProtectedRoute><Catalog /></ProtectedRoute>} />
            <Route path="/app/projects" element={<ProtectedRoute><Projects /></ProtectedRoute>} />
            <Route path="/app/admin" element={<AdminRoute><Admin /></AdminRoute>} />
            <Route path="/app/packages" element={<AdminRoute><PackageLibrary /></AdminRoute>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster
          theme="dark"
          position="bottom-right"
          toastOptions={{
            style: {
              background: "#121214",
              border: "1px solid #27272A",
              color: "#FAFAFA",
              fontFamily: "JetBrains Mono, monospace",
              fontSize: "12px",
            },
          }}
        />
      </AuthProvider>
    </div>
  );
}

export default App;
